import type { Plant, PlantInput } from './types';
import { CATEGORIES } from './constants';

const BASE = import.meta.env.VITE_API_URL ?? '';

async function req<T>(path: string, options?: RequestInit): Promise<T> {
  const pin = localStorage.getItem('app_pin') || '';
  const res = await fetch(`${BASE}${path}`, {
    headers: { 
      'Content-Type': 'application/json', 
      'x-app-pin': pin,
      ...options?.headers 
    },
    ...options,
  });
  
  if (res.status === 401) {
    localStorage.removeItem('app_pin');
    window.location.reload();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error ?? 'Request failed');
  }
  return res.json() as Promise<T>;
}

export const api = {
  /** List all plants */
  async getPlants(): Promise<Plant[]> {
    const plants = await req<Plant[]>('/api/plants');
    return plants.sort((a, b) => {
      const idxA = CATEGORIES.indexOf(a.category as any);
      const idxB = CATEGORIES.indexOf(b.category as any);
      const validA = idxA !== -1 ? idxA : CATEGORIES.length;
      const validB = idxB !== -1 ? idxB : CATEGORIES.length;
      
      if (validA !== validB) {
        return validA - validB;
      }
      
      const matchA = a.notes?.match(/Nr:\s*(\d+)/i);
      const matchB = b.notes?.match(/Nr:\s*(\d+)/i);
      const numA = matchA ? parseInt(matchA[1], 10) : 99999;
      const numB = matchB ? parseInt(matchB[1], 10) : 99999;
      
      if (numA !== numB) {
        return numA - numB;
      }

      return a.german_name.localeCompare(b.german_name);
    });
  },

  /** Create a new plant */
  createPlant(data: PlantInput): Promise<Plant> {
    return req<Plant>('/api/plants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Create multiple plants */
  createPlantsBulk(data: PlantInput[]): Promise<{ success: boolean; count: number }> {
    return req<{ success: boolean; count: number }>('/api/plants/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Update a plant */
  updatePlant(id: string, data: Partial<PlantInput>): Promise<Plant> {
    return req<Plant>(`/api/plants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Delete a plant */
  deletePlant(id: string): Promise<{ success: boolean }> {
    return req<{ success: boolean }>(`/api/plants/${id}`, {
      method: 'DELETE',
    });
  },
};
